import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";

const KEY = "adit-display-currency";
const CurrencyContext = createContext(null);

export function CurrencyProvider({ children }) {
  const [fx, setFx] = useState(null);
  const [code, setCode] = useState(() => localStorage.getItem(KEY) || "AED");

  useEffect(() => {
    let cancelled = false;
    const load = () => api.get("/fx/rates")
      .then(({ data }) => { if (!cancelled) setFx(data); })
      .catch(() => { if (!cancelled) setFx((prev) => prev
        || { base: "AED", rates: { AED: 1 }, currencies: [], unavailable: true }); });
    load();
    // A tab left open for days would otherwise keep stale rates.
    const onFocus = () => load();
    const timer = setInterval(load, 6 * 60 * 60 * 1000);
    window.addEventListener("focus", onFocus);
    return () => {
      cancelled = true;
      clearInterval(timer);
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  const setCurrency = useCallback((next) => {
    setCode(next);
    localStorage.setItem(KEY, next);
  }, []);

  const value = useMemo(() => {
    const rates = fx?.rates || { AED: 1 };
    const list = fx?.currencies || [];
    const active = rates[code] ? code : "AED";

    // Display-only: AED remains the billing currency for every subscription.
    const convert = (amount, from = "AED") => {
      if (amount == null || active === from) return null;
      const fromRate = rates[from];
      const toRate = rates[active];
      if (!fromRate || !toRate) return null;
      return (Number(amount) / fromRate) * toRate;
    };

    const format = (amount, currency = active) => {
      if (amount == null) return "—";
      const d = list.find((c) => c.code === currency)?.decimals ?? 2;
      return `${currency} ${Number(amount).toLocaleString(undefined, {
        minimumFractionDigits: d, maximumFractionDigits: d,
      })}`;
    };

    return {
      code: active, setCurrency, currencies: list, convert, format,
      isBase: active === "AED",
      asOf: fx?.provider_updated_at || fx?.cached_at || null,
      stale: !!fx?.stale, unavailable: !!fx?.unavailable, ready: !!fx,
      attribution: fx?.attribution_url,
    };
  }, [fx, code, setCurrency]);

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
}

export const useCurrency = () => useContext(CurrencyContext);
